/**
 * How a turn's steps read in the thread's fold (S-509): which tools are steps,
 * which already show as a card or line of their own (events), which are
 * plumbing (hidden), and which go in the turn's computer card; each step's
 * past-tense label; and how a run of one kind groups ("Read 3 pages"). Pure,
 * so the Worker counts steps the way the SPA shows them.
 */

/** Where a step goes in the fold. */
export type StepRole = "step" | "computer" | "event" | "hidden";

/** Tools whose work shows as its own card or line in the thread (a task card, "Updated its notes"). */
const EVENT_TOOLS = [
  "memory_edit",
  "memory_write",
  "memory_note",
  "task_create",
  "task_update",
  "doc_create",
  "doc_edit",
  // Slice 6i: its card in the chat is the record.
  "app_publish",
  "routine_create",
  "routine_update",
  "routine_notify",
  "area_create",
  "area_update",
  "area_brief_edit",
  "chat_area",
  "doc_area",
  "chat_settle",
  "computer_handoff",
  "chat_ask",
] as const;

/** Plumbing a person needn't see: finding an app's action, the progress line. */
const HIDDEN_TOOLS = [
  "integrations_list",
  "integrations_search_actions",
  "integrations_get_schema",
  "integrations_read_guide",
  "approval_withdraw",
  "progress_card",
] as const;

/** Tools that never count toward "N steps", for the Worker's count. */
export const UNCOUNTED_TOOLS: readonly string[] = [...EVENT_TOOLS, ...HIDDEN_TOOLS];

const EVENTS = new Set<string>(EVENT_TOOLS);
const HIDDEN = new Set<string>(HIDDEN_TOOLS);

/** A call made from inside a code mode script: `<script's call id>/c<n>` (S-493). */
const fromScript = (callId: string) => callId.includes("/");

export function stepRole(tool: string, kind: string): StepRole {
  if (EVENTS.has(tool)) return "event";
  if (HIDDEN.has(tool)) return "hidden";
  return kind === "browser" ? "computer" : "step";
}

/** A step as the fold needs it: a turn's step from `GET …/steps`, `detail` the Worker's line. */
export type FoldStep = {
  call_id: string;
  tool: string;
  kind: string;
  status: "ok" | "error" | "unknown";
  error: string | null;
  params: string | null;
  detail: string;
  duration_ms: number | null;
  /** The page the step ended on (slice 6f): a browser step's `result.url`. */
  url?: string | null;
  /** The page's title, as `url`. */
  title?: string | null;
  /** Its picture of the page (slice 6f): `saved`, `sign_in`, or none. */
  frame?: "saved" | "sign_in" | null;
};

/** A string field of a step's params, which may be JSON cut short; null when it isn't there. */
export function paramField(params: string | null, ...names: string[]): string | null {
  if (!params) return null;
  for (const name of names) {
    const m = params.match(new RegExp(`"${name}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)`));
    if (m) {
      try {
        return JSON.parse(`"${m[1]}"`) as string;
      } catch {
        return m[1];
      }
    }
  }
  return null;
}

/** A URL's host, without `www.`; null when it isn't a URL. */
function hostOf(url: string): string | null {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return null;
  }
}

const cut = (s: string, max: number) => (s.length > max ? `${s.slice(0, max - 1)}…` : s);

/**
 * "Ran curl on canadiantire.ca", "Ran python3 sum.py": a command's program,
 * and the host it reached or its first argument, at most 40 characters.
 */
export function commandSummary(command: string): string {
  // A quoted argument (a header, a message) says nothing about where it went.
  const words = command
    .replace(/'[^']*'|"[^"]*"/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const program = (words[0] ?? "").split("/").at(-1) ?? "";
  if (!program) return "Ran a command";
  const url = words.find((w) => /^(https?:\/\/|[a-z0-9-]+(\.[a-z0-9-]+)+\/)/i.test(w));
  const host = url ? hostOf(url.includes("://") ? url : `https://${url}`) : null;
  if (host) return cut(`Ran ${program} on ${host}`, 40);
  const arg = words.slice(1).find((w) => !w.startsWith("-"));
  return cut(arg ? `Ran ${program} ${arg}` : `Ran ${program}`, 40);
}

/** Apps whose action slugs don't read well title-cased. */
const APP_NAMES: Record<string, string> = {
  GMAIL: "Gmail",
  GOOGLECALENDAR: "Google Calendar",
  GOOGLEDRIVE: "Google Drive",
  GOOGLEDOCS: "Google Docs",
  GOOGLESHEETS: "Google Sheets",
  GOOGLETASKS: "Google Tasks",
  NOTION: "Notion",
  DROPBOX: "Dropbox",
  SLACK: "Slack",
};

/** An app call's app and action, from its detail ("GMAIL_SEND_EMAIL · you@example.com"). */
export function appAction(detail: string): { app: string; action: string } {
  const slug = (detail.split(" · ")[0] ?? "").trim();
  const known = Object.keys(APP_NAMES).find((k) => slug.startsWith(`${k}_`));
  if (known) {
    return {
      app: APP_NAMES[known],
      action: slug
        .slice(known.length + 1)
        .toLowerCase()
        .replace(/_/g, " "),
    };
  }
  const [head, ...rest] = slug.split("_");
  const app = head ? head[0] + head.slice(1).toLowerCase() : "an app";
  return { app, action: rest.join(" ").toLowerCase() };
}

const words = (tool: string) => tool.replace(/_/g, " ");

/** What a label is made from: the step's detail line, and whether it failed. */
type Said = { d: string; failed: boolean; s: FoldStep };

/** "Read a.com/x", or "Couldn't read a.com/x" when it failed. */
const did =
  (done: string, couldnt: string, none: string) =>
  ({ d, failed }: Said) =>
    `${failed ? couldnt : done} ${d || none}`;

/** "Searched docs for "x"", or "Searched docs" with no query. */
const searched =
  (what: string, field?: string[]) =>
  ({ d, s }: Said) => {
    const q = field ? paramField(s.params, ...field) : d;
    return q ? `${what} for "${q}"` : what;
  };

const command = ({ d, failed, s }: Said) => {
  const ran = commandSummary(paramField(s.params, "command") ?? d);
  return failed ? ran.replace(/^Ran /, "Couldn't run ") : ran;
};

const fixed = (text: string) => () => text;

/** Each tool's past-tense line (S-509's step copy). */
const LABELS: Record<string, (said: Said) => string> = {
  search_web: (said) =>
    said.failed ? "Couldn't search the web" : searched("Searched the web")(said),
  read_page: did("Read", "Couldn't read", "a page"),
  integrations_execute: ({ d, failed }) =>
    `${failed ? "Couldn't reach" : "Used"} ${appAction(d).app}`,
  board: fixed("Looked at the board"),
  doc_read: did("Read", "Couldn't open", "a doc"),
  doc_search: searched("Searched docs"),
  file_search: searched("Searched files"),
  file_get: did("Opened", "Couldn't open", "a file"),
  file_put: ({ d }) => `Kept ${d || "a file"} in Files`,
  memory_read: fixed("Read its notes"),
  memory_search: searched("Searched its notes", ["query"]),
  read: did("Read", "Couldn't read", "a file"),
  write: did("Wrote", "Couldn't save", "a file"),
  edit: did("Edited", "Couldn't save", "a file"),
  bash: command,
  computer_bash: command,
  codemode: ({ failed }) => (failed ? "The script stopped" : "Ran a script"),
  browser_open: did("Opened", "Couldn't open", "a page"),
  browser_snapshot: fixed("Looked at the page"),
  browser_click: ({ failed }) => (failed ? "Couldn't click on the page" : "Clicked on the page"),
  // Never what was typed: it may be a password.
  browser_type: fixed("Typed in a field"),
  browser_press: ({ s }) => `Pressed ${paramField(s.params, "key") ?? "a key"}`,
  browser_scroll: fixed("Scrolled"),
  browser_back: fixed("Went back"),
  browser_screenshot: fixed("Took a screenshot"),
  // S-525: a page kept as a doc, named by its title.
  browser_save: ({ s, failed }) => {
    const title = paramField(s.params, "title");
    if (failed) return "Couldn't keep the page";
    return title ? `Kept ${title} in Docs` : "Kept the page in Docs";
  },
};

/** A step's past-tense line: "Searched the web for "bikes"", "Couldn't read a.com/x". */
export function stepLabel(s: FoldStep): string {
  const said = { d: s.detail.trim(), failed: s.status === "error", s };
  const label = LABELS[s.tool];
  if (label) return label(said);
  return said.failed ? `Couldn't ${words(s.tool)}` : `Used ${words(s.tool)}`;
}

/** The run a tool's steps join, and what the run says for `n` of them. */
const GROUPS: Record<string, { key: string; label: (n: number) => string }> = {};
const group = (tools: string[], key: string, label: (n: number) => string) => {
  for (const tool of tools) GROUPS[tool] = { key, label };
};
const times = (n: number) => `${n} times`;
group(["search_web"], "search_web", (n) => `Searched the web ${times(n)}`);
group(["read_page"], "read_page", (n) => `Read ${n} pages`);
group(["board"], "board", (n) => `Looked at the board ${times(n)}`);
group(["doc_read"], "doc_read", (n) => `Read ${n} docs`);
group(["doc_search"], "doc_search", (n) => `Searched docs ${times(n)}`);
group(["file_search"], "file_search", (n) => `Searched files ${times(n)}`);
group(["file_get"], "file_get", (n) => `Opened ${n} files`);
group(["memory_read", "memory_search"], "notes", (n) => `Read its notes ${times(n)}`);
group(["read"], "files-read", (n) => `Read ${n} files`);
group(["write", "edit"], "files-changed", (n) => `Changed ${n} files`);
group(["bash", "computer_bash"], "command", (n) => `Ran ${n} commands`);

/** Which run of steps a step joins: same key, one after another, all ok. Null never groups. */
function groupOf(s: FoldStep): { key: string; label: (n: number) => string } | null {
  if (s.status === "error") return null;
  if (s.tool === "integrations_execute") {
    const { app } = appAction(s.detail);
    return { key: `app:${app}`, label: (n) => `Used ${app} ${times(n)}` };
  }
  return GROUPS[s.tool] ?? null;
}

/** A row of the fold. */
export type FoldEntry =
  | { type: "step"; step: FoldStep; label: string; children: FoldStep[] }
  | { type: "group"; key: string; label: string; steps: FoldStep[]; ms: number }
  | { type: "computer"; steps: FoldStep[]; sites: string[]; ms: number };

type StepEntry = Extract<FoldEntry, { type: "step" }>;
type ComputerEntry = Extract<FoldEntry, { type: "computer" }>;

const msOf = (steps: FoldStep[]) => steps.reduce((sum, s) => sum + (s.duration_ms ?? 0), 0);

/**
 * The hosts a turn's browser visited, in order, each once: the page each step
 * ended on (`url`, slice 6f), so a click's counts, else the one it was asked
 * to open.
 */
export function sitesOf(steps: Array<Pick<FoldStep, "kind" | "params" | "url">>): string[] {
  const sites: string[] = [];
  for (const s of steps) {
    if (s.kind !== "browser") continue;
    const url = s.url ?? paramField(s.params, "url", "targetUrl");
    // A web page's host: not a new tab's or a blank page's.
    const host = url && /^https?:\/\//i.test(url) ? hostOf(url) : null;
    if (host && !sites.includes(host)) sites.push(host);
  }
  return sites;
}

/** "a.com → b.com → c.com", or "a.com → … → d.com" past three. */
export function breadcrumb(sites: string[]): string {
  if (sites.length <= 3) return sites.join(" → ");
  return `${sites[0]} → … → ${sites.at(-1)}`;
}

/** A step joining the row before it, when they're of one run: a group of two, or one more. */
function joinRun(entries: FoldEntry[], step: FoldStep): boolean {
  const run = groupOf(step);
  const last = entries.at(-1);
  if (!run || !last) return false;
  if (last.type === "group" && last.key === run.key) {
    last.steps.push(step);
    last.label = run.label(last.steps.length);
    last.ms = msOf(last.steps);
    return true;
  }
  if (last.type !== "step" || last.children.length > 0 || groupOf(last.step)?.key !== run.key) {
    return false;
  }
  const steps = [last.step, step];
  entries[entries.length - 1] = {
    type: "group",
    key: run.key,
    label: run.label(2),
    steps,
    ms: msOf(steps),
  };
  return true;
}

/** "Ran a script · 2 reads": a script's line once its own calls are in. */
function scriptLabel(e: StepEntry): string {
  const n = e.children.length;
  if (e.step.status === "error" || n === 0) return e.label;
  return `Ran a script · ${n} ${n === 1 ? "read" : "reads"}`;
}

/**
 * A turn's steps as the fold's rows, in order: the turn's browser steps in
 * one computer card where the first was; a script's own calls under it; a
 * run of one kind as a group; events and plumbing left out.
 */
export function foldEntries(steps: FoldStep[]): FoldEntry[] {
  const entries: FoldEntry[] = [];
  const scripts = new Map<string, StepEntry>();
  let computer: ComputerEntry | null = null;
  for (const step of steps) {
    if (fromScript(step.call_id)) {
      scripts.get(step.call_id.split("/")[0])?.children.push(step);
      continue;
    }
    const role = stepRole(step.tool, step.kind);
    if (role === "computer") {
      if (!computer) entries.push((computer = { type: "computer", steps: [], sites: [], ms: 0 }));
      computer.steps.push(step);
    } else if (role === "step" && !joinRun(entries, step)) {
      const entry: StepEntry = { type: "step", step, label: stepLabel(step), children: [] };
      if (step.tool === "codemode") scripts.set(step.call_id, entry);
      entries.push(entry);
    }
  }
  if (computer) {
    computer.sites = sitesOf(computer.steps);
    computer.ms = msOf(computer.steps);
  }
  for (const e of scripts.values()) e.label = scriptLabel(e);
  return entries;
}
